// The combobox look (Combobox.scss) for free text: an input whose value is
// whatever the user types, with matching suggestions in a listbox (ADR 0024).
import '../Combobox/Combobox.scss'
import { useId, useMemo, useRef, type ComponentProps } from 'react'
import { MdOutlineClose } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import {
  assignRef,
  buildModel,
  defaultFilter,
  ListboxPopup,
  optionId,
  useListbox,
  type ListboxOption
} from '../Listbox'

export interface AutosuggestProps extends Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'children'
> {
  value: string
  /** Every change: typing, clearing, or picking a suggestion (`isPick`). */
  onChange: (value: string, isPick: boolean) => void
  /** Candidate values. Matching ones show while typing, prefix matches first. */
  suggestions: readonly string[]
  /** Suggestions are being fetched: the popup shows a spinner. */
  isLoading?: boolean
  /** Show a clear button while there is text. */
  isClearable?: boolean
  size?: 'sm' | 'md'
}

/** Prefix matches first, then other substring matches, each in the given order. */
export function rankSuggestions(suggestions: readonly string[], query: string): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...suggestions]
  const prefix: string[] = []
  const other: string[] = []
  for (const s of suggestions) {
    const lower = s.toLowerCase()
    if (lower.startsWith(q)) prefix.push(s)
    else if (lower.includes(q)) other.push(s)
  }
  return [...prefix, ...other]
}

/**
 * Free-text input with suggestions (WAI-ARIA 1.2 combobox, list
 * autocomplete). Typing never changes what was typed: arrows choose a
 * suggestion, Enter or a click takes it. No matches, no popup.
 */
export function Autosuggest({
  value,
  onChange,
  suggestions,
  isLoading = false,
  isClearable = false,
  size = 'md',
  disabled,
  readOnly,
  className,
  ref,
  onKeyDown,
  onBlur,
  ...rest
}: AutosuggestProps) {
  const listId = `autosuggest-${useId()}-listbox`
  const inputRef = useRef<HTMLInputElement | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const isInteractive = !(disabled || readOnly)

  const options = useMemo<ListboxOption[]>(
    () =>
      rankSuggestions(suggestions, value)
        // The exact current text is not a suggestion.
        .filter((s) => s !== value)
        .map((s) => ({ value: s, label: s })),
    [suggestions, value]
  )
  const model = useMemo(() => buildModel(options, '', defaultFilter), [options])

  const listbox = useListbox({
    model,
    selectedIndex: -1,
    isAutoActive: false,
    onPick: (option) => {
      onChange(option.value, true)
    }
  })
  const hasPopup = listbox.isOpen && (model.rows.length > 0 || isLoading)

  return (
    <div
      ref={anchorRef}
      className={clx(
        'combobox',
        'combobox--as-free',
        size !== 'md' && `combobox--is-${size}`,
        className
      )}
      data-disabled={disabled || undefined}
    >
      <input
        {...rest}
        ref={(node) => {
          inputRef.current = node
          assignRef(ref, node)
        }}
        type="text"
        role="combobox"
        className="combobox__input"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        readOnly={readOnly}
        aria-autocomplete="list"
        aria-expanded={hasPopup}
        aria-controls={hasPopup ? listId : undefined}
        aria-activedescendant={
          hasPopup && listbox.activeIndex >= 0 ? optionId(listId, listbox.activeIndex) : undefined
        }
        value={value}
        onChange={(event) => {
          if (!isInteractive) return
          onChange(event.target.value, false)
          if (!listbox.isOpen) listbox.open()
          listbox.resetActive()
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || !isInteractive) return
          if (event.key === 'Escape') return
          listbox.onKeyDown(event)
        }}
        onBlur={(event) => {
          onBlur?.(event)
          if (anchorRef.current?.contains(event.relatedTarget)) return
          listbox.close()
        }}
      />
      {isClearable && value !== '' && isInteractive && (
        <IconButton
          label="Clear"
          icon={<MdOutlineClose />}
          size="sm"
          hasTooltip={false}
          className="combobox__clear"
          onClick={() => {
            onChange('', false)
            inputRef.current?.focus()
          }}
        />
      )}
      {hasPopup && (
        <ListboxPopup
          id={listId}
          anchorRef={anchorRef}
          inputRef={inputRef}
          model={model}
          activeIndex={listbox.activeIndex}
          isSelected={() => false}
          onPick={listbox.pick}
          onActiveChange={listbox.setActiveIndex}
          onDismiss={listbox.close}
          isLoading={isLoading}
          size={size}
        />
      )}
    </div>
  )
}
